import {
  checklistsNamesTransformer,
  checklistsPageTransformer,
  checklistTransformer,
  templateChecklistTransformer,
} from './ChecklistTransformers';
import type {
  ChecklistAPI,
  ChecklistsNamesAPI,
  ChecklistsPageAPI,
} from './Definitions/APITypes';
import type {
  Checklist,
  ChecklistsNames,
  ChecklistsPage,
  NewChecklist,
  UpdatedChecklist,
} from './Definitions/Types';
import { gql } from '@apollo/client';

import type { DateRange, Sort, UUID } from '@cobliteam/common-types';

import graphQLRequester from '../../request/GraphQLRequester';

export class GraphQLChecklistsRequester {
  private allChecklistAttributes = `
    id
    version
    name
    template
    template_id
    created_at
    created_by
    fields {
      id
      type
      required
      title
      content {
        ... on CheckContent {
          checked
        }
        ... on TextContent {
          value
        }
        ... on SelectContent {
          options {
            id
            label
            checked
          }
        }
      }
    }
    context {
      app_version
      vehicle {
        device_id
        license_plate
        brand
        model
      }
    }
  `;

  private pagedChecklistsAtributes = `
    page {
      content {
        ${this.allChecklistAttributes}
      }
      last
      total_pages
      total_elements
      number_of_elements
      number
      size
      first
      empty
    }
  `;

  public getCompletedChecklists = (
    period?: DateRange,
    page?: number,
    size?: number,
    sort?: Sort,
    inputFilter?: string,
    nameFilters?: string[]
  ): Promise<ChecklistsPage> =>
    graphQLRequester
      .query<{ completedChecklists: ChecklistsPageAPI }>({
        query: gql`
          query GetCompletedChecklists($completedChecklistsToGet: GetCompletedChecklists) {
            completedChecklists(input: $completedChecklistsToGet) {
              ${this.pagedChecklistsAtributes}
            }
          }
        `,
        variables: {
          completedChecklistsToGet: {
            period,
            page,
            size,
            sort,
            inputFilter,
            nameFilters,
          },
        },
      })
      .then((result) => checklistsPageTransformer(result.completedChecklists));

  public getTemplateChecklists = (): Promise<ChecklistsPage> =>
    graphQLRequester
      .query<{ templateChecklists: ChecklistsPageAPI }>({
        query: gql`
          query {
            templateChecklists {
              ${this.pagedChecklistsAtributes}
            }
          }
        `,
      })
      .then((result) => checklistsPageTransformer(result.templateChecklists));

  public getChecklistsNames = (template: boolean): Promise<ChecklistsNames> =>
    graphQLRequester
      .query<{ checklistsNames: ChecklistsNamesAPI }>({
        query: gql`
          query GetChecklistsNames($checklistsNamesToGet: GetChecklistsNames) {
            checklistsNames(input: $checklistsNamesToGet) {
              names
            }
          }
        `,
        variables: { checklistsNamesToGet: { template } },
      })
      .then((result) => checklistsNamesTransformer(result.checklistsNames));

  public getChecklistById = (checklistId: UUID): Promise<Checklist> =>
    graphQLRequester
      .query<{ checklist: ChecklistAPI }>({
        query: gql`
          query GetChecklist($checklistToGet: GetChecklist) {
            checklist(input: $checklistToGet) {
              ${this.allChecklistAttributes}
            }
          }
        `,
        variables: { checklistToGet: { id: checklistId } },
      })
      .then((result) => checklistTransformer(result.checklist));

  public createChecklist = (newChecklist: NewChecklist): Promise<Checklist> =>
    graphQLRequester
      .mutate<{ addChecklist: ChecklistAPI }>({
        mutation: gql`
          mutation AddChecklist($checklistToCreate: NewChecklist) {
            addChecklist(input: $checklistToCreate) {
              ${this.allChecklistAttributes}
            }
          }
        `,
        variables: {
          checklistToCreate: templateChecklistTransformer(newChecklist),
        },
      })
      .then((result) => checklistTransformer(result.addChecklist));

  public updateChecklist = (
    id: UUID,
    updatedChecklist: UpdatedChecklist
  ): Promise<Checklist> =>
    graphQLRequester
      .mutate<{ updateChecklist: ChecklistAPI }>({
        mutation: gql`
          mutation UpdateChecklist($checklistToUpdate: UpdateChecklist) {
            updateChecklist(input: $checklistToUpdate){
              ${this.allChecklistAttributes}
            }
          }
        `,
        variables: {
          checklistToUpdate: {
            id,
            updatedChecklist: templateChecklistTransformer(updatedChecklist),
          },
        },
      })
      .then((result) => checklistTransformer(result.updateChecklist));

  public deleteChecklist = (checklistId: UUID): Promise<UUID> =>
    graphQLRequester
      .mutate<{ deleteChecklist: { id: UUID } }>({
        mutation: gql`
          mutation DeleteChecklist($checklistToDelete: DeleteChecklist) {
            deleteChecklist(input: $checklistToDelete) {
              id
            }
          }
        `,
        variables: { checklistToDelete: { id: checklistId } },
      })
      .then((result) => result.deleteChecklist.id);
}

export default GraphQLChecklistsRequester;
